// Mock data for Document Workspace prototype

export interface Document {
  id: string;
  name: string;
  type: 'review' | 'workbench';
  clauses: Clause[];
}

export interface Clause {
  id: string;
  title: string;
  type: ClauseType;
  content: string;
  position: number;
}

export type ClauseType =
  | 'definitions'
  | 'term'
  | 'indemnification'
  | 'liability'
  | 'confidentiality'
  | 'security'
  | 'termination'
  | 'ip'
  | 'payment'
  | 'general';

export interface Playbook {
  id: string;
  name: string;
  clauseType: ClauseType;
  goodItems: string[];
  avoidItems: string[];
  preferredItems: string[];
}

export interface Project {
  id: string;
  name: string;
  documents: Document[];
}

// Matter types (renamed from Engagement)
export interface Matter {
  id: string;
  name: string;
  createdAt: Date;
}

// Scope item (for contract outline with checkboxes)
export interface ScopeItem {
  id: string;
  clauseId: string;
  title: string;
  selected: boolean;
}

// New types for unified workspace

export type IssueSource = 'manual' | 'ai-generated';
export type IssueStatus = 'pending' | 'accepted' | 'rejected';
export type IssueSeverity = 'high' | 'medium' | 'low';

export interface Issue {
  id: string;
  title: string;
  description: string;
  severity: IssueSeverity;
  source: IssueSource;
  status: IssueStatus;
  clauseRef: string;
  documentId: string;
  documentName: string;
  createdAt: Date;
}

export interface Difference {
  id: string;
  clauseTitle: string;
  baseClauseId: string;
  reviewClauseId: string;
  baseText: string;
  reviewText: string;
  aiSummary: string;
}

export interface DifferenceDecision {
  type: 'accept-theirs' | 'keep-ours' | 'alternative';
  text?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// ============ MOCK DATA ============

export const mockPlaybooks: Playbook[] = [
  {
    id: 'pb-1',
    name: 'Indemnification Standards',
    clauseType: 'indemnification',
    goodItems: [
      'Mutual indemnification obligations',
      'Defense and control rights',
      'Notice requirements within 30 days',
      'Right to approve settlements',
    ],
    avoidItems: [
      'Open-ended indemnification',
      'One-way indemnification favoring vendor',
      'Indemnification for gross negligence',
      'No cap on indemnity obligations',
    ],
    preferredItems: [
      'Cap at 2x annual fees',
      'Carve-out for gross negligence/willful misconduct',
      'IP indemnification unlimited',
      'Survival period of 2 years post-termination',
    ],
  },
  {
    id: 'pb-2',
    name: 'Liability Limitations',
    clauseType: 'liability',
    goodItems: [
      'Mutual limitation of liability',
      'Exclusion of consequential damages',
      'Cap tied to contract value',
    ],
    avoidItems: [
      'Unlimited liability for any party',
      'One-sided limitation favoring vendor',
      'No carve-outs for IP/confidentiality',
    ],
    preferredItems: [
      'Cap at 12 months of fees paid',
      'Super-cap for data breach at 2x',
      'Carve-out for indemnification obligations',
    ],
  },
  {
    id: 'pb-3',
    name: 'Confidentiality Terms',
    clauseType: 'confidentiality',
    goodItems: [
      'Mutual confidentiality obligations',
      'Clear definition of confidential information',
      'Standard exceptions (public domain, independent development)',
    ],
    avoidItems: [
      'Perpetual confidentiality obligations',
      'No exceptions for legally required disclosure',
      'Overly broad definition capturing all information',
    ],
    preferredItems: [
      '3-5 year confidentiality period',
      'Return or destruction upon termination',
      'Permitted disclosure to advisors under NDA',
    ],
  },
];

export const mockDocuments: Document[] = [
  {
    id: 'doc-1',
    name: 'ACME_MSA_v7.pdf',
    type: 'review',
    clauses: [
      {
        id: 'clause-1',
        title: '1. Definitions',
        type: 'definitions',
        position: 1,
        content: `"Affiliate" means any entity that directly or indirectly controls, is controlled by, or is under common control with a party, where "control" means ownership of more than fifty percent (50%) of the voting securities.

"Confidential Information" means any non-public information disclosed by either party to the other, whether orally or in writing, that is designated as confidential or that reasonably should be understood to be confidential given the nature of the information.

"Services" means the software-as-a-service offerings provided by Supplier as described in the applicable Order Form.

"Documentation" means the user guides, online help, and release notes provided by Supplier for the Services.`,
      },
      {
        id: 'clause-2',
        title: '2. Term and Renewal',
        type: 'term',
        position: 2,
        content: `This Agreement shall commence on the Effective Date and continue for an initial term of three (3) years ("Initial Term"). Thereafter, this Agreement shall automatically renew for successive one (1) year periods ("Renewal Terms") unless either party provides written notice of non-renewal at least ninety (90) days prior to the end of the then-current term.

The fees for any Renewal Term shall be subject to an annual increase of up to five percent (5%) above the fees for the immediately preceding term, unless otherwise agreed in writing.`,
      },
      {
        id: 'clause-3',
        title: '3. Indemnification',
        type: 'indemnification',
        position: 3,
        content: `3.1 Supplier Indemnification. Supplier shall indemnify, defend, and hold harmless Customer and its officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to any claim that the Services infringe any third-party intellectual property rights.

3.2 Customer Indemnification. Customer shall indemnify, defend, and hold harmless Supplier from and against any claims arising from Customer's use of the Services in violation of this Agreement or applicable law.

3.3 Indemnification Procedures. The indemnified party shall provide prompt written notice of any claim and reasonable cooperation in the defense. The indemnifying party shall have sole control of the defense and settlement of any claim.`,
      },
      {
        id: 'clause-4',
        title: '4. Limitation of Liability',
        type: 'liability',
        position: 4,
        content: `4.1 LIMITATION ON INDIRECT DAMAGES. IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.

4.2 LIMITATION ON DIRECT DAMAGES. EXCEPT FOR BREACHES OF CONFIDENTIALITY OR INDEMNIFICATION OBLIGATIONS, EACH PARTY'S TOTAL CUMULATIVE LIABILITY SHALL NOT EXCEED THE AMOUNTS PAID BY CUSTOMER DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.

4.3 NO LIMITATION FOR CERTAIN DAMAGES. Nothing in this Agreement shall limit either party's liability for gross negligence, willful misconduct, or fraud.`,
      },
      {
        id: 'clause-5',
        title: '5. Confidentiality',
        type: 'confidentiality',
        position: 5,
        content: `5.1 Protection of Confidential Information. Each party agrees to protect the other party's Confidential Information using the same degree of care it uses to protect its own confidential information, but in no event less than reasonable care.

5.2 Permitted Disclosures. A party may disclose Confidential Information to its employees, contractors, and advisors who have a need to know and are bound by confidentiality obligations at least as protective as those herein.

5.3 Exclusions. Confidential Information does not include information that: (a) is or becomes publicly available through no fault of the receiving party; (b) was rightfully in the receiving party's possession prior to disclosure; (c) is independently developed without use of Confidential Information; or (d) is rightfully obtained from a third party without restriction.

5.4 Required Disclosures. A party may disclose Confidential Information if required by law, provided it gives reasonable advance notice and cooperates in seeking protective treatment.`,
      },
      {
        id: 'clause-6',
        title: '6. Data Security',
        type: 'security',
        position: 6,
        content: `6.1 Security Measures. Supplier shall implement and maintain appropriate administrative, technical, and physical safeguards to protect Customer Data, including encryption at rest and in transit, access controls, and regular security assessments.

6.2 Data Processing. Supplier shall process Customer Data only as necessary to provide the Services and in accordance with Customer's documented instructions.

6.3 Breach Notification. Supplier shall notify Customer within seventy-two (72) hours of discovering any unauthorized access to Customer Data and shall cooperate in investigating and remedying any such incident.

6.4 Compliance. Supplier represents that it maintains SOC 2 Type II certification and complies with applicable data protection laws.`,
      },
    ],
  },
  {
    id: 'doc-2',
    name: 'Supplier_SOW.docx',
    type: 'review',
    clauses: [
      {
        id: 'sow-1',
        title: '1. Scope of Work',
        type: 'general',
        position: 1,
        content: `This Statement of Work ("SOW") describes the implementation services to be provided by Supplier in connection with the Master Services Agreement dated [DATE] (the "MSA").

Supplier shall provide the following services:
- Initial platform configuration and setup
- Data migration from Customer's existing systems
- User training for up to 50 users
- Integration with Customer's SSO provider
- Go-live support for a period of 30 days`,
      },
      {
        id: 'sow-2',
        title: '2. Payment Terms',
        type: 'payment',
        position: 2,
        content: `The total fees for the services described in this SOW are $75,000 USD, payable as follows:

- 30% upon execution of this SOW
- 40% upon completion of data migration
- 30% upon go-live acceptance

All invoices are due within thirty (30) days of receipt. Late payments shall accrue interest at 1.5% per month or the maximum rate permitted by law, whichever is less.`,
      },
    ],
  },
  {
    id: 'doc-3',
    name: 'Security_Addendum.pdf',
    type: 'workbench',
    clauses: [
      {
        id: 'sec-1',
        title: 'Security Requirements',
        type: 'security',
        position: 1,
        content: `Standard security addendum template with baseline requirements for all vendor agreements.`,
      },
    ],
  },
];

// Compare document (counterparty version)
export const mockCompareDocument: Document = {
  id: 'doc-compare',
  name: 'ACME_MSA_v7_Counterparty.pdf',
  type: 'review',
  clauses: [
    {
      id: 'cp-clause-3',
      title: '3. Indemnification',
      type: 'indemnification',
      position: 3,
      content: `3.1 Supplier Indemnification. Supplier shall indemnify Customer against claims that the Services infringe third-party intellectual property rights, limited to direct damages only.

3.2 Customer Indemnification. Customer shall indemnify and hold harmless Supplier from any and all claims, including third-party claims, arising from Customer's use of the Services.

3.3 Indemnification Procedures. The indemnified party shall provide notice of any claim within 10 business days. The indemnifying party shall have sole control of the defense.`,
    },
    {
      id: 'cp-clause-4',
      title: '4. Limitation of Liability',
      type: 'liability',
      position: 4,
      content: `4.1 LIMITATION ON DAMAGES. SUPPLIER'S TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID IN THE SIX (6) MONTHS PRECEDING THE CLAIM.

4.2 NO EXCLUSIONS. This limitation applies to all claims regardless of cause.`,
    },
  ],
};

export const mockDifferences: Difference[] = [
  {
    id: 'diff-1',
    clauseTitle: 'Indemnification',
    baseClauseId: 'clause-3',
    reviewClauseId: 'cp-clause-3',
    baseText: '...indemnify, defend, and hold harmless Customer...from and against any and all claims, damages, losses...',
    reviewText: '...indemnify Customer against claims...limited to direct damages only.',
    aiSummary: 'Counterparty has narrowed indemnification scope by removing "defend and hold harmless" language and limiting to direct damages. This significantly reduces their exposure.',
  },
  {
    id: 'diff-2',
    clauseTitle: 'Indemnification - Customer Obligations',
    baseClauseId: 'clause-3',
    reviewClauseId: 'cp-clause-3',
    baseText: '...claims arising from Customer\'s use of the Services in violation of this Agreement or applicable law.',
    reviewText: '...any and all claims, including third-party claims, arising from Customer\'s use of the Services.',
    aiSummary: 'Customer indemnification has been broadened. The counterparty version removes the "violation" qualifier, making Customer liable for any claim related to use, not just misuse.',
  },
  {
    id: 'diff-3',
    clauseTitle: 'Indemnification - Notice Period',
    baseClauseId: 'clause-3',
    reviewClauseId: 'cp-clause-3',
    baseText: '...provide prompt written notice...',
    reviewText: '...provide notice within 10 business days.',
    aiSummary: 'Notice period changed from "prompt" to specific 10 business day requirement. This is more specific but could be problematic for complex claims that take time to evaluate.',
  },
  {
    id: 'diff-4',
    clauseTitle: 'Liability Cap',
    baseClauseId: 'clause-4',
    reviewClauseId: 'cp-clause-4',
    baseText: '...TWELVE (12) MONTHS PRECEDING THE CLAIM.',
    reviewText: '...SIX (6) MONTHS PRECEDING THE CLAIM.',
    aiSummary: 'Liability cap reduced from 12 months to 6 months of fees. This is a significant reduction in Supplier exposure.',
  },
  {
    id: 'diff-5',
    clauseTitle: 'Liability - Carve-outs',
    baseClauseId: 'clause-4',
    reviewClauseId: 'cp-clause-4',
    baseText: '...EXCEPT FOR BREACHES OF CONFIDENTIALITY OR INDEMNIFICATION OBLIGATIONS...',
    reviewText: '...This limitation applies to all claims regardless of cause.',
    aiSummary: 'All carve-outs removed. Counterparty wants liability cap to apply universally, including for confidentiality breaches and indemnification. This is a significant change.',
  },
];

export const mockProject: Project = {
  id: 'proj-1',
  name: 'Acme Corp - MSA Review',
  documents: mockDocuments,
};

// Mock Matters (for Matter selector dropdown)
export const mockMatters: Matter[] = [
  {
    id: 'matter-1',
    name: 'Acme Corp - MSA Review',
    createdAt: new Date('2025-12-15'),
  },
  {
    id: 'matter-2',
    name: 'BigCo NDA Negotiation',
    createdAt: new Date('2025-12-10'),
  },
  {
    id: 'matter-3',
    name: 'Supplier Agreement',
    createdAt: new Date('2025-12-01'),
  },
];

// Mock Workbench documents (playbook/reference docs)
export const mockWorkbenchDocs: Document[] = [
  {
    id: 'wb-1',
    name: 'Standard MSA Playbook.pdf',
    type: 'workbench',
    clauses: [],
  },
  {
    id: 'wb-2',
    name: 'Indemnification Standards.docx',
    type: 'workbench',
    clauses: [],
  },
  {
    id: 'wb-3',
    name: 'Security Addendum Template.pdf',
    type: 'workbench',
    clauses: [],
  },
];

export const mockIssues: Issue[] = [
  {
    id: 'issue-1',
    title: 'Uncapped Indemnification',
    description: 'The indemnification clause has no cap on obligations. Playbook recommends cap at 2x annual fees.',
    severity: 'high',
    source: 'ai-generated',
    status: 'pending',
    clauseRef: 'Clause 3.1',
    documentId: 'doc-1',
    documentName: 'ACME_MSA_v7.pdf',
    createdAt: new Date('2025-12-20'),
  },
  {
    id: 'issue-2',
    title: 'Missing Super-Cap for Data Breach',
    description: 'No enhanced liability cap for data breach scenarios. Consider negotiating 2x cap.',
    severity: 'medium',
    source: 'ai-generated',
    status: 'accepted',
    clauseRef: 'Clause 4.2',
    documentId: 'doc-1',
    documentName: 'ACME_MSA_v7.pdf',
    createdAt: new Date('2025-12-20'),
  },
  {
    id: 'issue-3',
    title: 'Auto-Renewal with Price Increase',
    description: 'Agreement auto-renews with up to 5% annual increase. May want to negotiate cap or opt-out.',
    severity: 'low',
    source: 'ai-generated',
    status: 'pending',
    clauseRef: 'Clause 2',
    documentId: 'doc-1',
    documentName: 'ACME_MSA_v7.pdf',
    createdAt: new Date('2025-12-20'),
  },
  {
    id: 'issue-4',
    title: 'Unclear Data Retention',
    description: 'Security clause does not specify data retention periods after termination.',
    severity: 'medium',
    source: 'manual',
    status: 'pending',
    clauseRef: 'Clause 6',
    documentId: 'doc-1',
    documentName: 'ACME_MSA_v7.pdf',
    createdAt: new Date('2025-12-21'),
  },
  {
    id: 'issue-5',
    title: 'Payment Terms - Interest Rate',
    description: '1.5% monthly interest on late payments is high. Standard is typically 1% or prime + 2%.',
    severity: 'low',
    source: 'manual',
    status: 'accepted',
    clauseRef: 'Clause 2',
    documentId: 'doc-2',
    documentName: 'Supplier_SOW.docx',
    createdAt: new Date('2025-12-21'),
  },
];

// Mock AI responses for the inspector actions
export const mockAIResponses: Record<string, Record<string, string>> = {
  'clause-3': {
    summarize: `This indemnification clause establishes mutual protection obligations between Supplier and Customer.

**Key Points:**
- Supplier indemnifies Customer for IP infringement claims
- Customer indemnifies Supplier for misuse of services
- Standard indemnification procedures apply (notice, cooperation, control of defense)

**Plain Language:** If someone sues you because of the other party's actions, that party will pay for your legal defense and any damages.`,

    risk: `**Risk Level: MEDIUM**

**Concerns Identified:**
1. **Asymmetric Coverage** - Supplier's indemnification is limited to IP claims only, while Customer's indemnification is broader ("violation of this Agreement or applicable law")

2. **No Cap on Indemnification** - Neither party's indemnification obligations are capped, creating unlimited exposure

3. **Sole Control of Defense** - The indemnifying party has "sole control" which may result in settlements unfavorable to the indemnified party

**Recommendation:** Negotiate for mutual indemnification scope and consider adding a cap tied to contract value.`,

    vsPlaybook: `**Playbook Comparison: Indemnification Standards**

**Coverage Check:**
- [MISSING] No indemnification cap - Playbook recommends cap at 2x annual fees
- [MISSING] No survival period specified - Playbook recommends 2 years post-termination
- [MISSING] No settlement approval rights - Playbook recommends right to approve settlements
- [MISSING] No notice timeline - Playbook recommends 30-day notice requirement
- [OK] Mutual structure (though asymmetric scope)
- [OK] Defense control rights
- [OK] Cooperation requirements

**Wording Comparison:**

Your clause says:
"Supplier shall indemnify, defend, and hold harmless Customer..."

Playbook preferred:
"Each party shall indemnify, defend, and hold harmless the other party, subject to a cap of 2x annual fees..."

**Key Differences:**
1. Playbook prefers explicit mutual structure in same clause
2. Playbook includes cap reference in indemnification language itself
3. Playbook includes "except for IP indemnification which shall be unlimited"

**Overall Alignment: 60%**`,
  },
  'clause-4': {
    summarize: `This clause limits each party's liability and excludes certain types of damages.

**Key Points:**
- Consequential and indirect damages are excluded
- Direct damages capped at 12 months of fees paid
- Carve-outs exist for gross negligence, willful misconduct, fraud
- Additional carve-outs for confidentiality and indemnification

**Plain Language:** If something goes wrong, neither side can sue for speculative "what if" losses, and the maximum payout is limited to what was paid in the last year.`,

    risk: `**Risk Level: LOW**

**Positive Elements:**
1. **Mutual limitation** - Both parties subject to same caps
2. **Reasonable cap** - 12 months of fees is market standard
3. **Appropriate carve-outs** - Gross negligence and willful misconduct excluded from cap

**Minor Concerns:**
1. **No super-cap for data breach** - Consider negotiating 2x cap for security incidents
2. **Indemnification carve-out** - Combined with uncapped indemnification, this creates exposure

**Overall:** Well-balanced clause that follows industry standards.`,

    vsPlaybook: `**Playbook Comparison: Liability Limitations**

**Coverage Check:**
- [MISSING] No super-cap for data breach - Playbook recommends 2x cap for security incidents
- [OK] Mutual limitation (matches playbook)
- [OK] 12 months cap (matches playbook recommendation)
- [OK] Carve-out for indemnification (matches playbook)
- [OK] Exclusion of consequential damages (matches playbook)

**Wording Comparison:**

Your clause says:
"EACH PARTY'S TOTAL CUMULATIVE LIABILITY SHALL NOT EXCEED THE AMOUNTS PAID BY CUSTOMER DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM."

Playbook preferred:
"Each party's total cumulative liability shall not exceed the amounts paid during the twelve (12) months preceding the claim; provided, however, that for claims arising from data security incidents, liability shall not exceed two times (2x) such amount."

**Key Differences:**
1. Playbook includes data breach super-cap in same sentence
2. Playbook uses sentence case rather than all caps
3. Very similar structure, primary difference is the proviso clause

**Overall Alignment: 85%**
Minor adjustment recommended to add super-cap proviso.`,
  },
};
